import { useAsesor } from "@/hooks/useAsesor";
import SectionTitle from "@/components/base/SectionTitle";
import AdvisorAccountPanel from "@/pages/asesor/components/AdvisorAccountPanel";
import { formatCOP, formatNumber } from "@/utils/format";

// Mi Inventario: desglose de lo comprado, lo consignado y lo que se debe.
const AsesorInventoryPage = () => {
  const {
    items,
    units,
    inventoryValue,
    potentialProfit,
    margin,
    compradoValue,
    compradoUnits,
    consignadoValue,
    consignedUnits,
    debt,
    movements,
  } = useAsesor();

  return (
    <div className="pb-24">
      <SectionTitle
        text="Mi Inventario y mi Cuenta"
        subtitle="Aquí ves lo que te queda, lo que ya compraste al admin, lo que tienes en consignación y lo que debes pagar."
      />

      <AdvisorAccountPanel
        compradoValue={compradoValue}
        compradoUnits={compradoUnits}
        consignadoValue={consignadoValue}
        consignedUnits={consignedUnits}
        debt={debt}
        movements={movements}
      />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4 mt-5">
        {[
          { icon: "ri-archive-2-line", label: "Referencias", value: formatNumber(items.length), bubble: "bg-primary-500/25 text-primary-100" },
          { icon: "ri-stack-line", label: "Unidades disponibles", value: formatNumber(units), bubble: "bg-secondary-500/25 text-secondary-200" },
          { icon: "ri-money-dollar-circle-line", label: "Valor a precio público", value: formatCOP(inventoryValue), bubble: "bg-accent-500/20 text-accent-200" },
          { icon: "ri-hand-coin-line", label: "Ganancia potencial", value: formatCOP(potentialProfit), bubble: "bg-accent-500/15 text-accent-100" },
        ].map((s) => (
          <div key={s.label} className="glass-panel rounded-lg p-4 animate-fade-up">
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.bubble}`}>
              <i className={`${s.icon} text-lg`} />
            </span>
            <p className="text-[11px] text-foreground-500 mt-3">{s.label}</p>
            <p className="text-lg md:text-xl font-heading font-extrabold text-foreground-950 mt-0.5 break-words">
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="glass-panel rounded-lg px-5 py-12 text-center mt-5">
          <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
            <i className="ri-inbox-archive-line text-2xl" />
          </span>
          <p className="text-sm text-foreground-700 font-semibold">Aún no tienes productos</p>
          <p className="text-xs text-foreground-500 mt-1">
            Juanka te asignará mercancía desde el panel de consignación.
          </p>
        </div>
      ) : (
        <div className="glass-panel rounded-lg overflow-hidden mt-5">
          <div className="px-4 py-3 border-b border-accent-500/15 flex items-center gap-2">
            <i className="ri-price-tag-3-line text-accent-300" />
            <p className="text-xs text-foreground-600">
              Precio costo asesor = precio público menos tu margen del {margin}%.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-foreground-500 border-b border-accent-500/15">
                  <th className="text-left font-semibold px-4 py-3">Producto</th>
                  <th className="text-center font-semibold px-4 py-3">Consignado</th>
                  <th className="text-center font-semibold px-4 py-3">Propio</th>
                  <th className="text-center font-semibold px-4 py-3">Total</th>
                  <th className="text-right font-semibold px-4 py-3">Costo asesor</th>
                  <th className="text-right font-semibold px-4 py-3">Ganancia / und</th>
                  <th className="text-right font-semibold px-4 py-3">Ganancia total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr
                    key={i.product.id}
                    className="border-b border-background-300/20 hover:bg-background-200/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="font-semibold text-foreground-950">{i.product.name}</p>
                      <p className="text-[11px] text-foreground-500">{i.product.sku}</p>
                    </td>
                    <td className="px-4 py-3 text-center text-secondary-200 font-semibold">
                      {formatNumber(i.consignedUnits)}
                    </td>
                    <td className="px-4 py-3 text-center text-foreground-800">
                      {formatNumber(i.ownedUnits)}
                    </td>
                    <td className="px-4 py-3 text-center text-foreground-950 font-bold">
                      {formatNumber(i.units)}
                    </td>
                    <td className="px-4 py-3 text-right text-foreground-700">
                      {formatCOP(i.costAdvisor)}
                    </td>
                    <td className="px-4 py-3 text-right text-accent-200 font-semibold">
                      {formatCOP(i.profit)}
                    </td>
                    <td className="px-4 py-3 text-right text-foreground-950 font-semibold">
                      {formatCOP(i.profit * i.units)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AsesorInventoryPage;